/**
 * Shared People IQ document-type manager.
 *
 * Used in two places:
 *   - Employee profile → Documents tab (as a dialog, gear icon)
 *   - Company Settings → "People IQ — Document Types" panel (inline)
 *
 * The master list drives the Doc Type dropdown on every employee profile.
 * Writes are Admin/Admin_addl only — enforced server-side (403 otherwise).
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Lock, ShieldAlert, Trash2, FileText } from "lucide-react";

// Friendly display labels for shipped types; custom types fall back to
// prettified names.
export const DOC_LABELS = {
  family_photograph: "Family Photo (For ESIC)",
  esic_photograph: "ESIC Photograph",
  nominee_proof: "Nominee Proof (EPFO)",
  blood_group_proof: "Blood Group Proof",
};

export function DocTypeList({ docTypes, onChanged }) {
  const [newType, setNewType] = useState("");
  const [newConfidential, setNewConfidential] = useState(false);
  const [newMandatory, setNewMandatory] = useState(false);
  const [busy, setBusy] = useState(false);

  const add = async () => {
    const name = newType.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/people/document-types", { name, is_confidential: newConfidential, is_mandatory: newMandatory });
      toast.success("Document type added");
      setNewType("");
      setNewConfidential(false);
      setNewMandatory(false);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleMandatory = async (t) => {
    setBusy(true);
    try {
      await api.patch(`/people/document-types/${t.name}`, { is_mandatory: !t.is_mandatory });
      toast.success(
        t.is_mandatory
          ? `"${DOC_LABELS[t.name] || t.name.replace(/_/g, " ")}" is now optional — future finalizations won't require it`
          : `"${DOC_LABELS[t.name] || t.name.replace(/_/g, " ")}" is now mandatory — required before finalization`
      );
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleConfidential = async (t) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/people/document-types/${t.name}`, { is_confidential: !t.is_confidential });
      toast.success(
        t.is_confidential
          ? `"${t.name.replace(/_/g, " ")}" is now a normal type (${data.documents_updated} document(s) updated)`
          : `"${t.name.replace(/_/g, " ")}" marked confidential (${data.documents_updated} document(s) updated)`
      );
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete document type "${t.name.replace(/_/g, " ")}"? Existing documents of this type will move to "Other".`)) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/people/document-types/${t.name}`);
      toast.success(data.moved_to_other > 0 ? `Deleted — ${data.moved_to_other} document(s) moved to Other` : "Document type deleted");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="text-xs text-slate-500">
        This list drives the Doc Type dropdown on every employee profile. Tick <b>Confidential</b> for types whose
        documents only the employee, HR Manager, Admin and Admin_addl may view. Tick <b>Mandatory</b> for types that
        must be uploaded before an employee record can be finalized (no waiver). Deleting a type moves its
        existing documents to <b>Other</b>.
      </div>
      <div className="flex gap-2 items-center flex-wrap mt-3">
        <Input value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="e.g. Medical Certificate"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} data-testid="new-doc-type-input" className="flex-1 min-w-[140px]" />
        <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer" title="Only employee + HR + Admin can view documents of this type">
          <input type="checkbox" checked={newConfidential} onChange={(e) => setNewConfidential(e.target.checked)} data-testid="new-doc-type-confidential" /> Confidential
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer" title="Must be uploaded before the employee record can be finalized">
          <input type="checkbox" checked={newMandatory} onChange={(e) => setNewMandatory(e.target.checked)} data-testid="new-doc-type-mandatory" /> Mandatory
        </label>
        <Button onClick={add} disabled={busy || !newType.trim()} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-doc-type-btn">Add</Button>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200 mt-3">
        {docTypes.map((t) => (
          <div key={t.name} className="flex items-center justify-between px-3 py-2" data-testid={`doc-type-row-${t.name}`}>
            <span className="text-sm capitalize text-slate-800 flex items-center gap-1.5">
              {DOC_LABELS[t.name] || t.name.replace(/_/g, " ")}
              {t.is_confidential && (
                <Badge className="bg-rose-100 text-rose-700 text-[9px] px-1 py-0" data-testid={`doc-type-conf-${t.name}`}>
                  <Lock className="w-2.5 h-2.5 mr-0.5" /> CONFIDENTIAL
                </Badge>
              )}
              {t.is_mandatory && (
                <Badge className="bg-amber-100 text-amber-800 text-[9px] px-1 py-0" data-testid={`doc-type-mand-${t.name}`}>
                  <ShieldAlert className="w-2.5 h-2.5 mr-0.5" /> MANDATORY
                </Badge>
              )}
            </span>
            <span className="flex items-center gap-1">
              {t.name !== "other" && (
                <button
                  onClick={() => toggleConfidential(t)}
                  disabled={busy}
                  className={`p-1 rounded transition ${t.is_confidential ? "text-rose-500 hover:text-slate-500" : "text-slate-300 hover:text-rose-500"}`}
                  title={t.is_confidential ? "Make normal (updates existing documents)" : "Mark confidential (updates existing documents)"}
                  data-testid={`toggle-conf-${t.name}`}
                >
                  <Lock className="w-3.5 h-3.5" />
                </button>
              )}
              {t.name !== "other" && (
                <button
                  onClick={() => toggleMandatory(t)}
                  disabled={busy}
                  className={`p-1 rounded transition ${t.is_mandatory ? "text-amber-500 hover:text-slate-500" : "text-slate-300 hover:text-amber-500"}`}
                  title={t.is_mandatory ? "Make optional (future finalizations won't require it)" : "Make mandatory (required before finalization)"}
                  data-testid={`toggle-mand-${t.name}`}
                >
                  <ShieldAlert className="w-3.5 h-3.5" />
                </button>
              )}
              {t.name === "other" ? (
                <span className="text-[10px] text-slate-400 italic">protected fallback</span>
              ) : (
                <button onClick={() => remove(t)} disabled={busy}
                  className="p-1 text-slate-300 hover:text-rose-500 transition" title="Delete type"
                  data-testid={`delete-doc-type-${t.name}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

export function ManageDocTypesDialog({ open, onClose, docTypes, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="manage-doc-types-dialog">
        <DialogHeader><DialogTitle>Manage Document Types</DialogTitle></DialogHeader>
        <DocTypeList docTypes={docTypes} onChanged={onChanged} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Self-loading inline panel for Company Settings (admin-only page).
export function DocTypesSettingsPanel() {
  const [docTypes, setDocTypes] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/people/document-types");
      setDocTypes(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load document types"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <Card data-testid="people-doc-types-card">
      <CardHeader>
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#7C1D3A]" /> People IQ — Document Types
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DocTypeList docTypes={docTypes} onChanged={load} />
      </CardContent>
    </Card>
  );
}
